import React, { useState } from 'react'
import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { isEmpty, size } from 'lodash'
import { PlusCircle as PlusCircleIcon } from 'react-feather'
import {
  Box,
  Button,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import { BaseDialog } from 'components'
import {
  CompanyDocumentItem,
  DocumentPreview,
  LinkConfirmationDialog,
} from './components'

import usePermissions from 'hooks/usePermissions'
import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const DocumentTemplateDialog = ({
  open,
  setOpen,
  documentTemplateName,
  companyPrivacyPolicies = [],
  companyDocumentTemplateId,
  evidenceId,
  linkedPrivacyPolicyIds = [],
  disabledInputs = false,
  onLink = () => {},
  onCreateDocument = () => {},
}) => {
  const { ACTIVE_STATUS_ID } = constants.documents
  const { PRIVACY_POLICIES } = constants.permissions

  const classes = useStyles()
  const snackbar = useSnackbar()
  const { permitted } = usePermissions()

  // Com um único documento não faz sentido exigir um clique só para revelar o conteúdo.
  const [selectedPrivacyPolicyId, setSelectedPrivacyPolicyId] = useState(
    size(companyPrivacyPolicies) === 1 ? companyPrivacyPolicies[0]?.id : null,
  )
  const [privacyPolicyToLink, setPrivacyPolicyToLink] = useState(null)
  const [linking, setLinking] = useState(false)

  const canCreateDocument = permitted(PRIVACY_POLICIES.CREATE)

  const hasActivePrivacyPolicy = companyPrivacyPolicies.some(
    (privacyPolicy) => privacyPolicy?.status === ACTIVE_STATUS_ID,
  )

  const isLinked = (privacyPolicy) =>
    linkedPrivacyPolicyIds.includes(privacyPolicy?.id)

  const handleSelect = (privacyPolicy) =>
    setSelectedPrivacyPolicyId((prev) =>
      prev === privacyPolicy?.id ? null : privacyPolicy?.id,
    )

  const handleCloseConfirmation = () => setPrivacyPolicyToLink(null)

  const handleConfirmLink = async () => {
    try {
      setLinking(true)

      const response =
        await service.dponet.privacyPolicies.createLinkedDocuments({
          privacyPolicyId: privacyPolicyToLink?.id,
          evidenceIds: [evidenceId],
        })

      snackbar.open({
        message: 'Documento vinculado como evidência com sucesso!',
        variant: 'success',
      })
      onLink(response?.data?.linkedDocuments || [])

      // Fecha a confirmação e o modal do modelo: o vínculo já está feito e a evidência da atividade
      // atrás dos modais foi atualizada, então mantê-los abertos só esconderia o resultado.
      // O `linking` não é resetado aqui de propósito — este componente desmonta junto com o modal.
      handleCloseConfirmation()
      setOpen(false)
    } catch (error) {
      setLinking(false)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Não foi possível vincular o documento como evidência',
        variant: 'error',
      })
    }
  }

  const mountCreateTooltip = () => {
    // Mesma ordem do botão da linha (DocumentTemplateAction): o bloqueio da evidência vem antes,
    // porque criar o documento aqui só serve para vinculá-lo em seguida a esta evidência.
    if (disabledInputs) {
      return 'Esta evidência não pode ser alterada no momento.'
    }

    if (!companyDocumentTemplateId) {
      return 'Este modelo de documento não está disponível para a sua organização.'
    }

    if (!canCreateDocument) {
      return 'Você não possui permissão para criar documentos.'
    }

    return ''
  }

  const createTooltip = mountCreateTooltip()

  return (
    <BaseDialog
      open={open}
      setOpen={setOpen}
      title={documentTemplateName}
      closeButtonText="Fechar"
      dialogSize="md"
      fullWidth
    >
      <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
        {/* Com a evidência bloqueada o aviso sobre documentos ativos daria uma saída que não existe
            ("gerar um novo"), então o motivo do bloqueio vem no lugar dele. */}
        {disabledInputs ? (
          <Alert severity="warning" variant="standard">
            <Typography>
              Esta evidência não pode ser alterada no momento, então não é
              possível vincular nem criar documentos por aqui.
            </Typography>
          </Alert>
        ) : (
          !hasActivePrivacyPolicy && (
            <Alert severity="info" variant="standard">
              <Typography>
                Somente documentos ativos podem ser vinculados como evidência.
                Você pode ativar um dos documentos abaixo ou criar um novo a
                partir deste modelo.
              </Typography>
            </Alert>
          )
        )}
        <Box>
          {/* Criar novo documento fica junto da lista, e não no rodapé: é uma alternativa ao que está
              listado ali, não uma ação de confirmação do modal. */}
          <Box className={classes.listHeader}>
            <Typography variant="h6">
              Documentos da organização a partir deste modelo
            </Typography>
            <Tooltip title={createTooltip}>
              <Box>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  startIcon={<PlusCircleIcon size={16} />}
                  onClick={onCreateDocument}
                  disabled={!!createTooltip}
                >
                  Criar novo documento
                </Button>
              </Box>
            </Tooltip>
          </Box>
          <Paper variant="outlined">
            <PerfectScrollbar
              options={{ wheelPropagation: true, useBothWheelAxes: false }}
              className={classes.documentItems}
            >
              {companyPrivacyPolicies.map((privacyPolicy, index) => (
                <Box key={privacyPolicy?.id}>
                  <CompanyDocumentItem
                    privacyPolicy={privacyPolicy}
                    isLinked={isLinked(privacyPolicy)}
                    isSelected={selectedPrivacyPolicyId === privacyPolicy?.id}
                    disabledLink={disabledInputs || linking}
                    disabledInputs={disabledInputs}
                    onSelect={() => handleSelect(privacyPolicy)}
                    onLink={() => setPrivacyPolicyToLink(privacyPolicy)}
                  />
                  {index < size(companyPrivacyPolicies) - 1 && <Divider />}
                </Box>
              ))}
            </PerfectScrollbar>
          </Paper>
        </Box>
        {!isEmpty(companyPrivacyPolicies) && (
          <DocumentPreview privacyPolicyId={selectedPrivacyPolicyId} />
        )}
      </Box>
      {!!privacyPolicyToLink && (
        <LinkConfirmationDialog
          open={!!privacyPolicyToLink}
          setOpen={handleCloseConfirmation}
          privacyPolicy={privacyPolicyToLink}
          loading={linking}
          onConfirm={handleConfirmLink}
        />
      )}
    </BaseDialog>
  )
}

DocumentTemplateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  documentTemplateName: PropTypes.string,
  companyPrivacyPolicies: PropTypes.array,
  companyDocumentTemplateId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  evidenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  linkedPrivacyPolicyIds: PropTypes.array,
  disabledInputs: PropTypes.bool,
  onLink: PropTypes.func,
  onCreateDocument: PropTypes.func,
}

export default DocumentTemplateDialog
